import {
  Box,
  Checkbox,
  CollapsibleCard,
  Grid,
  Skeleton,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { AutoComplete, SelectInput } from '@components';
import { Button } from '@components/Button';
import { FeatureFlags } from '@constants/featureFlags';
import {
  customerDetailsStore,
  fetchCustomerOfficeGroups,
  fetchCustomerOfficeGroupsCall,
  Group,
  ICustomerDetails,
  removeFromWalletAction,
  setCustomerDetailsStore,
  updateCustomerDetailsState,
} from '@store/customers/customerDetails';
import { IUserSearch } from '@store/customers/types';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { userStore } from '@store/user';
import { CloseIcon } from '@atgfr8/icons';
import { isAdmin, isManager, isReadonly, isSuperAdmin } from '@utils/utils';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { createMemo, createSignal, For, Show } from 'solid-js';

type CapacityGroupsProps = {
  value: number;
  label: string;
};

type GeneralOwnerShipProps = {
  capacityGroups: CapacityGroupsProps[];
};
type RenderItemsProps = {
  item2: IUserSearch;
};

const RenderItem = (props: RenderItemsProps) => (
  <Grid class="overflow-auto max-h-[600px] flex! flex-col!">
    <Typography variant="body2" class="font-medium!">
      {props.item2.name}
    </Typography>

    <Typography variant="body2">
      Office: {props.item2.office} - {props.item2.accountingGroup}
    </Typography>
    <Typography variant="body2">Email: {props.item2.userName}</Typography>
    <Typography variant="body2">Phone: {props.item2.phone}</Typography>
  </Grid>
);

const GeneralOwnerShip = (props: GeneralOwnerShipProps) => {
  const [localAccountOfficeId, setLocalAccountOfficeId] = createSignal<
    string | number
  >(customerDetailsStore.customer.accountManagerOfficeId ?? '');
  const isOperationsRepEnabled = isFeatureFlagEnabled(
    FeatureFlags.OperationsRep,
  );

  const [isLoading, setIsLoading] = createSignal<boolean>(false);
  const [removeFromWalletLoading, setRemoveFromWalletLoading] =
    createSignal<boolean>(false);

  const fetchAssignGroupData = async (officeId: number) => {
    try {
      setIsLoading(true);
      await fetchCustomerOfficeGroups(officeId, Number(localAccountOfficeId()));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssignGroupChange = (values: Partial<ICustomerDetails>) => {
    updateCustomerDetailsState({
      ...customerDetailsStore.customer,
      ...values,
    });
  };

  const editable = () => {
    const roles = userStore.user.roles || [];
    return (
      roles.includes('Admin') ||
      roles.includes('Manager') ||
      roles.includes('Recruiter') ||
      roles.includes('Supervisor') ||
      isFeatureFlagEnabled(FeatureFlags.AssignMultiofficeGroupsForCustomer)()
    );
  };

  const handleGroupCheckboxChange = (group: Group, setFalse: boolean) => {
    if (!editable()) return;
    const groups = [...customerDetailsStore.customer.groups];
    const index = groups.findIndex((g) => g.id === group.id);

    if (setFalse && index === -1) {
      groups.push({ ...group });
    } else if (!setFalse && index >= 0) {
      groups.splice(index, 1);
    }

    if (
      groups.some(
        (g) =>
          g.name === 'The Office' &&
          g.officeId === customerDetailsStore.customer.accountManagerOfficeId,
      )
    ) {
      setCustomerDetailsStore('customer', 'isPublic', true);
    } else {
      setCustomerDetailsStore('customer', 'isPublic', false);
    }

    handleAssignGroupChange({ groups });
  };

  const handleAssignGroupSelect = async (group: Group) => {
    await fetchAssignGroupData(group.id);
  };

  const handleGrouplistSelect = (item: IUserSearch, name: string) => {
    updateCustomerDetailsState({
      [`${name}ManagerName`]: item.name,
      [`${name}ManagerId`]: item.id,
    });
  };

  const RenderLoadOptions = (props: { groups: Group[] | undefined }) => {
    const localGroups = () => (props.groups ?? []).map((x) => x.id);
    return (
      <Box
        class={`bg-[white] rounded ${
          customerDetailsStore.assignOfficeGroup.length === 0 ||
          customerDetailsStore.assignOfficeGroup.every((group) =>
            localGroups().includes(Number(group.value)),
          )
            ? 'border-none!'
            : 'border-[#026EA14D] border-2 p-2 '
        }`}
      >
        <Show
          when={!isLoading()}
          fallback={<Skeleton variant="rectangular" width="100%" />}
        >
          <For each={customerDetailsStore.assignOfficeGroup}>
            {(group) => {
              return (
                <Box
                  class={`${
                    localGroups().includes(Number(group.value)) ? 'hidden' : ''
                  }`}
                >
                  <Checkbox
                    size="xs"
                    color="info"
                    class="ml-[-9px]"
                    label={group.label}
                    checked={false}
                    onChange={() => {
                      handleGroupCheckboxChange(group, true);
                    }}
                    dataTestId={`general-ownership-assign-group-checkboxInput-${group.value}`}
                  />
                </Box>
              );
            }}
          </For>
        </Show>
      </Box>
    );
  };

  const DisableChangeManager = () => {
    return !(
      isAdmin() ||
      (customerDetailsStore.customer.status !== 'Dormant' &&
        (userStore.user.id === customerDetailsStore.customer.accountManagerId ||
          userStore.user.id === customerDetailsStore.customer.salesManagerId ||
          isManager()))
    );
  };

  const handleRemoveFromWallet = async () => {
    const customer = customerDetailsStore.customer;
    const payload = {
      id: customer.id,
      comment: 'I want another customer',
    };
    setRemoveFromWalletLoading(true);
    await removeFromWalletAction(payload);
    setRemoveFromWalletLoading(false);
  };

  const notAllowedToClickPublic = createMemo(() => {
    return customerDetailsStore.assignOfficeGroup.some((x) => {
      return (
        x.officeId !== customerDetailsStore.customer.accountManagerOfficeId
      );
    });
  });

  return (
    <CollapsibleCard startTitle="Ownership" class="mb-2">
      <Grid container spacing={2} class="mb-3!">
        <Grid item size={6}>
          <AutoComplete
            endpoint="user/autocomplete/?term="
            version="v3"
            responseFieldName="name"
            onItemSelect={async (item) => {
              const { officeId = '' } = item as { officeId: number };
              setLocalAccountOfficeId(officeId);
              handleGrouplistSelect(item as IUserSearch, 'account');
              await fetchAssignGroupData(officeId as number);
              const filteredGroups =
                customerDetailsStore.customer.groups.filter(
                  (group) => group.officeId === (item as IUserSearch).officeId,
                );
              updateCustomerDetailsState({
                groups: filteredGroups,
              });
            }}
            id="autocomplete-accountManagerName"
            label="Account Manager"
            value={customerDetailsStore.customer.accountManagerName ?? ''}
            renderItem={(item) => {
              const item2 = item as IUserSearch;
              return <RenderItem item2={item2} />;
            }}
            onClearValue={() => {
              updateCustomerDetailsState({
                accountManagerName: '',
                accountManagerId: null,
              });
            }}
            class="w-full"
            disabled={DisableChangeManager()}
          />
        </Grid>
        <Grid item size={6}>
          <AutoComplete
            endpoint="user/autocomplete/?term="
            version="v3"
            responseFieldName="name"
            onItemSelect={(item) =>
              handleGrouplistSelect(item as IUserSearch, 'sales')
            }
            id="autocomplete-field-user"
            label="Sales Manager"
            value={customerDetailsStore.customer.salesManagerName ?? ''}
            renderItem={(item) => {
              const item2 = item as IUserSearch;
              return <RenderItem item2={item2} />;
            }}
            onClearValue={() => {
              updateCustomerDetailsState({
                salesManagerName: '',
                salesManagerId: null,
              });
            }}
            class="w-full"
            disabled={DisableChangeManager()}
          />
        </Grid>
        <Grid item size={12} class="min-w-0">
          <Checkbox
            color="info"
            label="When checked, this customer (and all associated loads) will be visible to everyone in the same office as the selected Account Manager."
            size="xs"
            name="isPublic"
            class="ml-[-9px] w-full max-w-full [&>span]:whitespace-normal! [&>span]:wrap-break-word!"
            checked={customerDetailsStore.customer.isPublic}
            onChange={async (value) => {
              if (!value) {
                const newgroups = [] as Group[];
                customerDetailsStore.customer.groups.forEach((g) => {
                  if (
                    g.name === 'The Office' &&
                    g.officeId ===
                      customerDetailsStore.customer.accountManagerOfficeId
                  ) {
                    // do nothing
                  } else {
                    newgroups.push(g);
                  }
                });
                updateCustomerDetailsState({
                  isPublic: value,
                  groups: newgroups,
                });
              } else {
                const newOfficeGroup =
                  customerDetailsStore.assignOfficeGroup.find(
                    (x) =>
                      x.name === 'The Office' &&
                      x.officeId ===
                        customerDetailsStore.customer.accountManagerOfficeId,
                  );
                if (newOfficeGroup) {
                  updateCustomerDetailsState({
                    isPublic: value,
                    groups: [
                      newOfficeGroup,
                      ...customerDetailsStore.customer.groups,
                    ],
                  });
                } else {
                  if (customerDetailsStore.customer.accountManagerOfficeId) {
                    // this is more than likely due to "The Office" group not being available
                    const officeDetails = await fetchCustomerOfficeGroupsCall(
                      customerDetailsStore.customer.accountManagerOfficeId,
                      true,
                    );
                    if (!officeDetails?.isSuccess) return;

                    const theOfficeGroup = officeDetails.value.find(
                      (g) =>
                        g.name === 'The Office' &&
                        g.officeId ===
                          customerDetailsStore.customer.accountManagerOfficeId,
                    );
                    if (!theOfficeGroup) return;

                    updateCustomerDetailsState({
                      isPublic: value,
                      groups: [
                        theOfficeGroup,
                        ...customerDetailsStore.customer.groups,
                      ],
                    });
                  }
                }
              }
            }}
            disabled={
              (!isManager() && !isAdmin() && !isSuperAdmin()) ||
              !customerDetailsStore.customer.isPublicEditable ||
              notAllowedToClickPublic()
            }
            dataTestId="general-ownership-is-public-checkboxInput"
          />
        </Grid>
        <Show
          when={
            customerDetailsStore.customer.claimedBy != null && !isReadonly()
          }
        >
          <Grid size={12} p={2}>
            <Box class="flex justify-between items-center bg-[#f5f5f5]! border border-[#e3e3e3]! rounded-sm p-2! flex-wrap">
              <Typography variant="body2" class="font-medium!">
                Dormant Account Claimed By
                <span class="mx-1 font-bold">
                  {customerDetailsStore.customer.claimedBy}
                </span>
              </Typography>
              <Button
                label="Remove"
                size="sm"
                color="error"
                endIcon={<CloseIcon size={20} />}
                onClick={handleRemoveFromWallet}
                disabled={removeFromWalletLoading()}
                isLoading={removeFromWalletLoading()}
                dataTestId="general-ownership-remove-from-wallet-button"
              />
            </Box>
          </Grid>
        </Show>
      </Grid>
      <Show when={isOperationsRepEnabled()}>
        <Grid container spacing={1} class="mb-3!">
          <Grid item size={6}>
            <AutoComplete
              endpoint="user/autocomplete/?term="
              version="v3"
              responseFieldName="name"
              onItemSelect={(item: IUserSearch) => {
                updateCustomerDetailsState({
                  operationsRepId: item.id,
                  operationsRepName: item.name,
                  operationsRepOfficeId: item.officeId,
                });
              }}
              id="autocomplete-operationsRep"
              label="Operations Rep"
              value={customerDetailsStore.customer.operationsRepName ?? ''}
              renderItem={(item) => {
                const item2 = item as IUserSearch;
                return <RenderItem item2={item2} />;
              }}
              onClearValue={() => {
                updateCustomerDetailsState({
                  operationsRepId: null,
                  operationsRepName: null,
                  operationsRepOfficeId: null,
                });
              }}
              class="w-full"
              disabled={DisableChangeManager()}
            />
          </Grid>
        </Grid>
      </Show>
      <Grid container spacing={2}>
        <Grid item size={12}>
          {isAdmin() ||
          isFeatureFlagEnabled(
            FeatureFlags.AssignMultiofficeGroupsForCustomer,
          )() ? (
            <AutoComplete
              class="bg-white!"
              endpoint="office/autocomplete/?term="
              version="v3"
              responseFieldName="name"
              onItemSelect={(item) => handleAssignGroupSelect(item as Group)}
              id="select-office-manage-group-section"
              label="Assign Group"
              value=""
              placeholder="Select Office"
            />
          ) : (
            <Box class="text-[12px] font-bold">Assign Groups</Box>
          )}
          <Grid class="overflow-auto max-h-[500px] flex! flex-col!">
            <Box class="mt-2 pl-[8px]">
              <Typography variant="body2">
                All users from selected groups will have visibility to this
                customer (and all associated loads).
              </Typography>
            </Box>
            <Box class="flex flex-wrap p-2">
              <For
                each={customerDetailsStore.customer.groups.filter(
                  (group) => isAdmin() || group.name !== 'The Office',
                )}
              >
                {(group) => (
                  <Box class="mr-2">
                    <Checkbox
                      size="xs"
                      color="info"
                      class="ml-[-9px]"
                      label={`${
                        Boolean(group.office)
                          ? `${group.name} (${group.office})`
                          : `${group.name}`
                      }`}
                      checked={true}
                      onChange={() => {
                        handleGroupCheckboxChange(group, false);
                      }}
                      dataTestId={`general-ownership-customer-group-checkboxInput-${group.id}`}
                    />
                  </Box>
                )}
              </For>
            </Box>
            <RenderLoadOptions groups={customerDetailsStore.customer.groups} />
          </Grid>
          <Show when={customerDetailsStore.assignOfficeGroup.length === 0}>
            <Box class="bg-[#f5f5f5] p-2 my-2 text-center border border-[#e3e3e3] rounded-sm">
              -- You have no groups --
            </Box>
          </Show>
        </Grid>
        {(isAdmin() ||
          isFeatureFlagEnabled(
            FeatureFlags.AllowCapacityGroupCustomerAssignment,
          )()) && (
          <Grid item size={12}>
            <SelectInput
              label="Capacity Group"
              placeholder="Select Capacity Group"
              menuItems={props.capacityGroups.map((g) => ({
                label: g.label,
                value: g.label,
              }))}
              onChange={(val) => {
                updateCustomerDetailsState({
                  capacityGroupId:
                    props.capacityGroups.find((g) => g.label === val)?.value ??
                    null,
                });
              }}
              value={
                props.capacityGroups.find(
                  (g) =>
                    g.value ===
                    (customerDetailsStore.customer
                      .capacityGroupId as unknown as number),
                )?.label ?? ''
              }
              dataTestId={formatDataTestId('Capacity group', 'selectInput')}
            />
          </Grid>
        )}
      </Grid>
    </CollapsibleCard>
  );
};

export default GeneralOwnerShip;
